import type { Store } from '@blocksuite/affine/store';
import {
  addBlobId,
  classifyPageSubdocId,
  collectDatabaseCellBlobIds,
  DATABASE_ROW_SHARD_ROWS_MAP_KEY,
  type DatabaseBlobCellKeys,
  databaseBlockBlobCellKeys,
  pageBlockIdsOfKind,
  pagePlacedBlockIds,
} from '@streamlain/database-identity';
import { Array as YArray, Map as YMap } from 'yjs';
export type SnapshotExportArchiveEntry = {
  readonly sourceDocId: string;
  readonly store: Store;
};

export type BlobColumnLookup = ReadonlyMap<string, DatabaseBlobCellKeys>;

export function collectSnapshotExportBlobIds(
  pages: readonly Store[],
  rootDocIds: readonly string[],
  rowShardDocIds: readonly string[]
): string[] {
  const entries = collectSnapshotExportArchiveEntries(
    pages,
    rootDocIds,
    rowShardDocIds
  );
  const blobIds = new Set<string>();
  const blobColumnLookup = collectBlobColumnLookup(entries);

  for (const entry of entries) {
    const structure = entry.store.pageStructure;
    pagePlacedBlockIds(structure.tree()).forEach(id => {
      const block = structure.blockFields(id);
      if (!(block instanceof YMap)) return;

      addBlobId(blobIds, block.get('prop:sourceId'));
    });

    const rowShard = classifyPageSubdocId(
      entry.sourceDocId,
      entry.store.workspace.id
    );
    if (rowShard?.kind !== 'database-row-shard' || rowShard.local) continue;

    const columns = blobColumnLookup.get(rowShard.databaseId);
    if (!columns) continue;

    collectDatabaseCellBlobIds(
      blobIds,
      readYValue(entry.store.spaceDoc.getMap(DATABASE_ROW_SHARD_ROWS_MAP_KEY)),
      columns
    );
  }

  return [...blobIds].sort();
}

export function collectSnapshotExportArchiveEntries(
  pages: readonly Store[],
  rootDocIds: readonly string[],
  rowShardDocIds: readonly string[]
): SnapshotExportArchiveEntry[] {
  const entries = new Map<string, SnapshotExportArchiveEntry>();
  for (const page of pages) {
    entries.set(page.id, { sourceDocId: page.id, store: page });
  }

  const workspace = pages[0]?.workspace;
  if (!workspace) {
    return [...entries.values()];
  }

  for (const docId of [...rootDocIds, ...rowShardDocIds]) {
    const store = workspace.getDoc(docId)?.getStore({ id: docId });
    if (store) {
      entries.set(docId, { sourceDocId: docId, store });
    }
  }

  return [...entries.values()];
}

export function readYValue(value: unknown): unknown {
  if (value instanceof YMap) {
    return Object.fromEntries(
      Array.from(value.entries()).map(([key, item]) => [key, readYValue(item)])
    );
  }

  if (value instanceof YArray) {
    return value.toArray().map(readYValue);
  }

  if (Array.isArray(value)) {
    return value.map(readYValue);
  }

  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([key, item]) => [
        key,
        readYValue(item),
      ])
    );
  }

  return value;
}

export function collectBlobColumnLookup(
  entries: readonly SnapshotExportArchiveEntry[]
): BlobColumnLookup {
  const lookup = new Map<string, DatabaseBlobCellKeys>();

  entries.forEach(entry => {
    const root = classifyPageSubdocId(
      entry.sourceDocId,
      entry.store.workspace.id
    );
    // A database root keeps its block in its own document, a page in its parts.
    const blocks: Array<readonly [string, unknown]> =
      root?.kind === 'database-root'
        ? [
            [
              root.databaseId,
              entry.store.spaceDoc.getMap('blocks').get(root.databaseId),
            ],
          ]
        : pageBlockIdsOfKind(
            entry.store.pageStructure.readSource(),
            'affine:database'
          ).map(id => [id, entry.store.pageStructure.blockFields(id)] as const);
    blocks.forEach(([blockId, block]) => {
      if (
        !(block instanceof YMap) ||
        block.get('sys:flavour') !== 'affine:database'
      ) {
        return;
      }

      collectDatabaseBlockColumnLookup(
        lookup,
        block,
        databaseBlockBlobCellKeys(block),
        blockId
      );
    });
  });

  return lookup;
}

export function collectDatabaseBlockColumnLookup(
  lookup: Map<string, DatabaseBlobCellKeys>,
  block: YMap<unknown>,
  columns: DatabaseBlobCellKeys,
  blockId?: string
): void {
  if (blockId) {
    lookup.set(blockId, columns);
  }

  const databaseId = block.get('prop:databaseId');
  if (typeof databaseId === 'string' && databaseId) {
    lookup.set(databaseId, columns);
  }
}
